import { useCallback, useEffect, useRef, useState } from "react";
import { runScriptReviewUploadFlow } from "@/features/script-review/api/script-review.api";
import { Sentry } from "@/lib/sentry";

export type ScriptReviewUploadState =
  | "idle"
  | "uploading"
  | "confirming"
  | "ready"
  | "failed";

export interface ScriptReviewUpload {
  file: File | null;
  uploadId: string | null;
  /** 0-100 for a direct-to-storage PUT; jumps to 100 on the stage-memory path. */
  progress: number;
  state: ScriptReviewUploadState;
  error: string | null;
  start: (file: File) => void;
  reset: () => void;
}

/**
 * Uploads one file as soon as it's picked, rather than on submit.
 *
 * The form needs the video in storage before the AI can transcribe it, so
 * waiting for "Run pre-flight" would mean the admin watches a loader that is
 * secretly a 60s upload. Starting here means the video is usually ready by the
 * time they've chosen a set.
 *
 * Picking a new file aborts the in-flight upload — the old bytes are of no use
 * to anyone, and letting both run would race to set `uploadId`.
 */
type TranslateFn = (key: string, defaultValue: string) => string;

export function useScriptReviewUpload(opts: {
  tenantId: string | null;
  purpose?: string;
  /** Normalized content type (browser MIME for .csv is unreliable). */
  contentTypeFor?: (file: File) => string;
  t?: TranslateFn;
}): ScriptReviewUpload {
  const [file, setFile] = useState<File | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [state, setState] = useState<ScriptReviewUploadState>("idle");
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Only the newest upload may write state — an aborted one must stay silent.
  const runIdRef = useRef(0);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    runIdRef.current += 1;
    setFile(null);
    setUploadId(null);
    setProgress(0);
    setState("idle");
    setError(null);
  }, []);

  const start = useCallback(
    (next: File) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const runId = ++runIdRef.current;
      const isStale = () => runIdRef.current !== runId;

      setFile(next);
      setUploadId(null);
      setProgress(0);
      setError(null);
      setState("uploading");

      if (!opts.tenantId) {
        setState("failed");
        setError(
          opts.t?.("scriptReview.errors.noActiveOrg", "No active organization") ??
            "No active organization",
        );
        return;
      }

      void (async () => {
        try {
          const id = await runScriptReviewUploadFlow({
            tenantId: opts.tenantId!,
            file: next,
            purpose: opts.purpose,
            contentType: opts.contentTypeFor?.(next),
            onProgress: (p) => {
              if (isStale()) return;
              setProgress(p);
              // The PUT is done; `confirm` is the short tail after it.
              if (p >= 100) setState("confirming");
            },
            signal: controller.signal,
          });
          if (isStale()) return;
          setUploadId(id);
          setProgress(100);
          setState("ready");
        } catch (e) {
          if (isStale() || controller.signal.aborted) return;
          setState("failed");
          if (!(e instanceof Error && e.name === "AbortError")) {
            Sentry.captureException(e);
          }
          setError(
            opts.t?.("scriptReview.upload.failed", "Upload failed — pick the file again.") ??
              "Upload failed — pick the file again.",
          );
        }
      })();
    },
    [opts.tenantId, opts.purpose, opts.contentTypeFor, opts.t],
  );

  // Don't leave an upload running for a form the user has navigated away from.
  useEffect(() => () => abortRef.current?.abort(), []);

  return { file, uploadId, progress, state, error, start, reset };
}
